import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Skeleton } from '../../components/ui/skeleton';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import { HelpCircle } from 'lucide-react';

// Import refactored lesson components
import { 
  LessonHeader, 
  LessonPlayer, 
  LessonActions,
  LessonContent,
  LessonSidebar,
  LessonAssistant,
  LessonGuide,
  SimulationProgress
} from '../../components/rpat/lesson';

const RPATLesson = () => {
  const { topicId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection - no screenshots, copy, right-click
  useContentProtection(true);
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  
  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [sectionStartTime, setSectionStartTime] = useState(null);
  
  // Assistant state
  const [showAssistant, setShowAssistant] = useState(false);
  
  // Lesson Guide state
  const [showGuide, setShowGuide] = useState(false);
  
  // Refs
  const audioRef = useRef(null);
  const simulationSectionRef = useRef(null);

  // Fetch lesson data
  const fetchLesson = useCallback(async () => {
    try {
      setLoading(true);
      
      // Start the topic if not already started
      await api.post(`/rpat/topics/${topicId}/start`);
      
      // Get topic details with next_topic info
      const topicRes = await api.get(`/rpat/topics/${topicId}`);
      setTopic(topicRes.data);
      
      // Get lesson content
      const lessonRes = await api.get(`/rpat/topics/${topicId}/lesson`);
      setLesson(lessonRes.data);
      setCurrentSectionIndex(lessonRes.data.current_section_index || 0);
      
      // Show guide for Phase 4 topics on first visit
      const isPhase4 = topicRes.data?.phase_id === 'phase_4';
      const hasSeenGuide = localStorage.getItem(`rpat_guide_${topicId}`);
      if (isPhase4 && !hasSeenGuide) {
        setTimeout(() => setShowGuide(true), 500);
        localStorage.setItem(`rpat_guide_${topicId}`, 'true');
      }
      
    } catch (err) {
      console.error('Failed to fetch lesson:', err);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  }, [api, topicId]);

  // Initial fetch and cleanup
  useEffect(() => {
    fetchLesson();
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      window.speechSynthesis?.cancel();
    };
  }, [fetchLesson]);

  // Computed values
  const currentSection = lesson?.sections?.[currentSectionIndex];
  const completedSections = lesson?.completed_sections || [];
  const isCurrentCompleted = currentSection && completedSections.includes(currentSection.id);
  const allSectionsCompleted = lesson?.sections?.every(s => completedSections.includes(s.id));
  const progressPct = (completedSections.length / (lesson?.sections?.length || 1)) * 100;
  
  // Topic progress info
  // topic_completed is true ONLY when the actual mock test (assessment) is passed
  // This is different from mock_test_passed which is also set by typing tests
  const topicCompleted = topic?.user_progress?.topic_completed || topic?.user_progress?.assessment_passed || false;
  const mockTestScore = topic?.user_progress?.mock_test_score;
  const typingTestPassed = topic?.user_progress?.typing_test_passed || false;
  const hasTypingTest = topic?.typing_test_number;
  const nextTopic = topic?.next_topic;
  const isLastInPhase = topic?.is_last_in_phase;
  
  // Phase 4 simulation requirements
  const simulationRequirements = topic?.simulation_requirements;
  const isPhase4 = topic?.phase_id === 'phase_4';

  // Scroll to simulation section
  const scrollToSimulations = () => {
    simulationSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch TTS audio for current section
  const fetchAudio = useCallback(async () => {
    const section = lesson?.sections?.[currentSectionIndex];
    if (!section) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    window.speechSynthesis?.cancel();
    setIsPlaying(false);
    
    try {
      const response = await api.post(`/rpat/topics/${topicId}/section/${section.id}/audio`);
      
      if (response.data.audio_base64) {
        setAudioUrl(`data:audio/mp3;base64,${response.data.audio_base64}`);
      } else if (response.data.audio_url) {
        setAudioUrl(response.data.audio_url);
      } else {
        setAudioUrl(null);
      }
    } catch (err) {
      console.error('Failed to fetch audio:', err);
      setAudioUrl(null);
    }
  }, [api, topicId, lesson, currentSectionIndex]);

  // Fetch audio when section changes
  useEffect(() => {
    if (lesson?.sections?.[currentSectionIndex]) {
      fetchAudio();
    }
  }, [lesson, currentSectionIndex, fetchAudio]);

  // Listen for voice preference changes
  useEffect(() => {
    const handleVoiceChange = () => {
      setAudioUrl(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      window.speechSynthesis?.cancel();
      setIsPlaying(false);
      if (lesson?.sections?.[currentSectionIndex]) {
        fetchAudio();
      }
    };
    
    window.addEventListener('voicePreferencesChanged', handleVoiceChange);
    return () => window.removeEventListener('voicePreferencesChanged', handleVoiceChange);
  }, [lesson, currentSectionIndex, fetchAudio]);

  // Browser fallback TTS
  const speakTextBrowser = useCallback((text) => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    
    synth.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = isMuted ? 0 : 1;
    
    const voices = synth.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Daniel')
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onend = () => setIsPlaying(false);
    synth.speak(utterance);
    setIsPlaying(true);
    setSectionStartTime(Date.now());
  }, [isMuted]);

  // Toggle play/pause
  const togglePlay = () => {
    if (!currentSection) return;
    
    window.speechSynthesis?.cancel();
    
    if (audioUrl && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          setSectionStartTime(prev => prev || Date.now());
        }).catch(err => {
          console.error('Audio playback error:', err);
          toast.error('Audio playback failed. Trying browser audio...');
          speakTextBrowser(currentSection.ai_script);
        });
      }
    } else {
      const synth = window.speechSynthesis;
      if (isPlaying) {
        synth?.cancel();
        setIsPlaying(false);
      } else {
        speakTextBrowser(currentSection.ai_script);
      }
    }
  };

  // Handle audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };
    
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    
    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  // Navigate to section
  const goToSection = (index) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    window.speechSynthesis?.cancel();
    setIsPlaying(false);
    setAudioUrl(null);
    setCurrentSectionIndex(index);
    setSectionStartTime(null);
  };

  // Complete current section
  const completeSection = async () => {
    if (!currentSection || isCurrentCompleted) return;
    
    try {
      const timeSpent = sectionStartTime ? Math.round((Date.now() - sectionStartTime) / 1000) : 60;
      
      await api.post(`/rpat/topics/${topicId}/section/${currentSection.id}/complete`, null, {
        params: { time_spent_seconds: timeSpent }
      });
      
      // Refresh lesson data
      const lessonRes = await api.get(`/rpat/topics/${topicId}/lesson`);
      setLesson(lessonRes.data);
      
      toast.success('Section completed!');
      
      // Auto-advance to next section if available
      if (currentSectionIndex < lesson.sections.length - 1) {
        setCurrentSectionIndex(currentSectionIndex + 1);
        setSectionStartTime(null);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        window.speechSynthesis?.cancel();
        setIsPlaying(false);
        setAudioUrl(null);
      }
    } catch (err) {
      console.error('Failed to complete section:', err);
      toast.error('Failed to save progress');
    }
  };

  // Finish lesson and navigate to test/next step
  const finishLesson = async () => {
    try {
      const response = await api.post(`/rpat/topics/${topicId}/complete`);
      const result = response.data;
      
      // Determine next step based on what's required and what's already done
      const hasTypingTest = topic?.typing_test_number;
      const hasMockTest = topic?.mock_test_id;
      const typingTestDone = typingTestPassed || result?.typing_test_passed;
      const assessmentDone = topicCompleted || result?.topic_fully_completed;
      
      // Check if simulations are required (Phase 4 topics)
      const hasSimulations = simulationRequirements?.has_simulations;
      const simulationsComplete = simulationRequirements?.all_complete;
      const nextSimulation = simulationRequirements?.next_simulation;
      
      // Phase 4: Check simulations first
      if (hasSimulations && !simulationsComplete) {
        // Simulations not complete - redirect to simulation
        if (nextSimulation) {
          toast.info(`Complete the ${nextSimulation.title} simulation to proceed`, {
            description: 'Score 80+ to pass each simulation'
          });
          navigate(`/recruiter-simulation?topic=${topicId}&sim=${nextSimulation.id}&type=${nextSimulation.scenario_type}&difficulty=${nextSimulation.difficulty}`);
        } else {
          scrollToSimulations();
          toast.info('Complete all simulations to unlock the assessment');
        }
        return;
      }
      
      if (hasTypingTest && !typingTestDone) {
        // Typing test required but not passed yet
        toast.success(`Lesson completed! Now complete Typing Test #${topic.typing_test_number}`);
        navigate(`/rpat/typing-tests?test=${topic.typing_test_number}`);
      } else if (hasMockTest && !assessmentDone) {
        // Mock test (assessment) required but not passed yet
        const message = hasSimulations && simulationsComplete 
          ? 'Simulations complete! Now take the final assessment.'
          : 'Typing test complete! Now take the assessment.';
        toast.success(message);
        navigate(`/rpat/assessment/${topic.mock_test_id}`);
      } else if (result?.topic_fully_completed || assessmentDone) {
        // Topic is fully complete - navigate to next
        if (nextTopic) {
          toast.success('Topic completed! Moving to next topic.');
          navigate(`/rpat/lesson/${nextTopic.id}`);
        } else if (isLastInPhase) {
          const phaseNum = parseInt(topic?.phase_id?.replace('phase_', ''), 10) || 0;
          if (phaseNum >= 1 && phaseNum <= 4) {
            navigate('/rpat/certification/job-mastery');
            toast.success('Phase completed! Time for Job Simulations.');
          } else {
            navigate('/rpat');
            toast.success('Phase completed!');
          }
        } else {
          navigate('/rpat');
          toast.success('Lesson completed!');
        }
      } else {
        // Fallback - go back to RPAT dashboard
        navigate('/rpat');
        toast.success('Progress saved!');
      }
    } catch (err) {
      console.error('Failed to complete lesson:', err);
      toast.error('Failed to complete lesson. Please try again.');
    }
  };

  // Loading state
  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-64" />
              <Skeleton className="h-96" />
            </div>
            <Skeleton className="h-96" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header with Guide Button for Phase 4 */}
        <div className="flex items-center justify-between">
          <LessonHeader topic={topic} />
          {isPhase4 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowGuide(true)}
              className="flex items-center gap-2"
              data-testid="lesson-guide-btn"
            >
              <HelpCircle className="h-4 w-4" />
              Lesson Guide
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Player Card with Actions */}
            <Card>
              <CardContent className="pt-6">
                <LessonPlayer
                  currentSection={currentSection}
                  currentSectionIndex={currentSectionIndex}
                  totalSections={lesson?.sections?.length || 0}
                  progressPct={progressPct}
                  isPlaying={isPlaying}
                  isMuted={isMuted}
                  onTogglePlay={togglePlay}
                  onPrevSection={() => currentSectionIndex > 0 && goToSection(currentSectionIndex - 1)}
                  onNextSection={() => currentSectionIndex < (lesson?.sections?.length || 0) - 1 && goToSection(currentSectionIndex + 1)}
                  onToggleMute={() => setIsMuted(!isMuted)}
                />
                
                {/* Action Buttons */}
                <LessonActions
                  topic={topic}
                  allSectionsCompleted={allSectionsCompleted}
                  isCurrentCompleted={isCurrentCompleted}
                  mockTestPassed={topicCompleted}
                  mockTestScore={mockTestScore}
                  hasTypingTest={hasTypingTest}
                  typingTestPassed={typingTestPassed}
                  simulationRequirements={simulationRequirements}
                  nextTopic={nextTopic}
                  isLastInPhase={isLastInPhase}
                  onCompleteSection={completeSection}
                  onFinishLesson={finishLesson}
                  onStartSimulations={scrollToSimulations}
                />
              </CardContent>
            </Card>

            {/* Simulation Progress Card (Phase 4 only) */}
            {isPhase4 && simulationRequirements?.has_simulations && (
              <div ref={simulationSectionRef}>
                <SimulationProgress
                  topicId={topicId}
                  simulationRequirements={simulationRequirements}
                />
              </div>
            )}

            {/* Transcript */}
            <LessonContent currentSection={currentSection} />
          </div>

          {/* Sidebar */}
          <LessonSidebar
            lesson={lesson}
            topic={topic}
            currentSectionIndex={currentSectionIndex}
            completedSections={completedSections}
            onSectionClick={goToSection}
            onOpenAssistant={() => setShowAssistant(true)}
          />
        </div>

        {/* Hidden Audio Element for TTS */}
        {audioUrl && (
          <audio 
            ref={audioRef} 
            src={audioUrl} 
            preload="auto"
            style={{ display: 'none' }}
          />
        )}

        {/* AI Assistant Modal */}
        <LessonAssistant
          isOpen={showAssistant}
          onClose={() => setShowAssistant(false)}
          currentSection={currentSection}
          topicTitle={topic?.title}
          api={api}
          topicId={topicId}
        />

        {/* Lesson Guide Popup */}
        <LessonGuide
          topic={topic}
          isOpen={showGuide}
          onClose={() => setShowGuide(false)}
          simulationRequirements={simulationRequirements}
          currentStep={allSectionsCompleted 
            ? (simulationRequirements?.all_complete ? 3 : 2) 
            : 1
          }
        />
      </div>
    </MainLayout>
  );
};

export default RPATLesson;
